/**
 * Document uploads — owned by this feature, implemented in `infrastructure/`
 * (§3.4, §4.1).
 *
 * Two categories, kept apart because they are read by different people. An
 * identity document is administrative; a medical report is not, and finance
 * must never reach it (ADR-004). The storage rules enforce the same split.
 */

export type DocumentCategory = 'identity' | 'medical';

/** What a document is proof of. Drives the checklist the employee sees. */
export type DocumentKind =
  | 'employee_id'
  | 'marriage_certificate'
  | 'birth_certificate'
  | 'medical_report';

export interface UploadedDocument {
  readonly id: string;
  readonly kind: DocumentKind;
  readonly category: DocumentCategory;
  readonly fileName: string;
  readonly sizeBytes: number;
  readonly uploadedAt: string;
  /** The dependant this proves, when it proves one. */
  readonly dependentId: string | null;
}

export type UploadFailureCode =
  | 'too_large'
  | 'wrong_type'
  | 'forbidden'
  | 'network'
  | 'unknown';

export class DocumentError extends Error {
  readonly code: UploadFailureCode;

  constructor(code: UploadFailureCode, message: string) {
    super(message);
    this.name = 'DocumentError';
    this.code = code;
  }
}

/** Mirrors storage.rules. Checked here so the employee is told before upload. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export const ALLOWED_MIME_TYPES: readonly string[] = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
];

export interface DocumentPort {
  listMyDocuments(): Promise<readonly UploadedDocument[]>;

  /**
   * A link to view one file, valid for this viewing only.
   *
   * Asked for at the moment of viewing rather than stored. A Firebase download
   * URL carries its own token: whoever holds the string reads the file, with
   * no further check. Persisting it in `employeeDocuments` — which the whole
   * committee may read — handed finance a working link to every medical
   * report, while `storage.rules` denied them the file itself. The rule was
   * right and unenforceable.
   *
   * Fetching it here puts the request back under those rules: finance asking
   * for a medical report is refused at the same boundary.
   */
  getViewUrl(documentId: string): Promise<string>;

  /**
   * Uploads one file.
   *
   * `category` decides the storage path, and therefore who can read it. It is
   * derived from the kind rather than passed by the caller, so a medical report
   * cannot be filed where finance can see it.
   */
  upload(file: File, kind: DocumentKind, dependentId?: string): Promise<void>;

  /** Replaces a bad scan. Employees cannot remove evidence, only supersede it. */
  replace(documentId: string, file: File): Promise<void>;
}
